// Ce qui distingue l'étude Brésil de l'étude États-Unis : catégories de vin (codes douaniers NCM / HTS),
// grandes zones d'origine, noms des États et des districts douaniers.
export type Pays = 'bra' | 'usa';

// ── Catégories de vin (partie 3) : regroupement des codes douaniers à 8 (NCM) ou 10 chiffres (HTS) ──
export interface Cat { key: string; test: (code: string, hs6: string) => boolean }
const BRA_CATS: Cat[] = [
  { key: 'cat.bra.still', test: (_c, h) => h === '220421' },
  { key: 'cat.bra.champ', test: (c) => c === '22041010' },
  { key: 'cat.bra.spark', test: (c, h) => h === '220410' && c !== '22041010' },
  { key: 'cat.must', test: (c, h) => h === '220430' || c === '22042220' || c === '22042920' },
  { key: 'cat.bra.bulk', test: (_c, h) => h === '220422' || h === '220429' },
];
const USA_CATS: Cat[] = [
  { key: 'cat.usa.red', test: (c) => ['2204215005', '2204215035', '2204215040'].includes(c) },
  { key: 'cat.usa.white', test: (c) => ['2204215015', '2204215050', '2204215055'].includes(c) },
  { key: 'cat.usa.spark', test: (_c, h) => h === '220410' },
  { key: 'cat.usa.over14', test: (c) => ['2204216000', '2204218030', '2204218060'].includes(c) },
  { key: 'cat.usa.other', test: (_c, h) => h === '220421' },
  { key: 'cat.usa.box', test: (_c, h) => h === '220422' },
  { key: 'cat.usa.bulk', test: (_c, h) => h === '220429' || h === '220430' },
];
export const CATS: Record<Pays, Cat[]> = { bra: BRA_CATS, usa: USA_CATS };
export function catOf(p: Pays, code: string, hs6: string): string {
  for (const c of CATS[p]) if (c.test(code, hs6)) return c.key;
  return CATS[p][CATS[p].length - 1].key;
}
// Vins bio certifiés (codes HTS « CERT ORGANIC ») — États-Unis seulement.
export const ORGANIC = new Set(['2204100065', '2204215035', '2204215050']);

// ── Grandes zones d'origine (partie 4.3) ──
const EUROPE = new Set(('ALB AND ARM AUT BEL BGR BIH BLR CHE CYP CZE DEU DNK ESP EST FIN FRA GBR GEO GRC HRV HUN IRL ISL ITA ' +
  'LIE LTU LUX LVA MDA MKD MLT MNE NLD NOR POL PRT ROU RUS SRB SVK SVN SWE TUR UKR XKX').split(' '));
const SOUTH_AM = new Set('ARG BOL BRA CHL COL ECU PER PRY URY VEN'.split(' '));
const OCEANIA = new Set(['AUS', 'NZL']);
export const ZONES = ['europe', 'southam', 'oceania', 'others'] as const;
export type Zone = typeof ZONES[number];
export function zoneOf(iso3: string): Zone {
  return EUROPE.has(iso3) ? 'europe' : SOUTH_AM.has(iso3) ? 'southam' : OCEANIA.has(iso3) ? 'oceania' : 'others';
}

// ── Districts douaniers américains : « NEW YORK CITY NY » → « New York City, NY » ──
const DIST_FIX: Record<string, string> = {
  'COLUMBIA-SNAKE': 'Columbia-Snake, OR-WA', 'NORFOLK/MOBILE/CHARLESTON': 'Norfolk / Mobile / Charleston',
  'DALLAS/FORT WORTH TX': 'Dallas-Fort Worth, TX', 'SAN JUAN PR': 'San Juan, PR', 'VIRGIN ISLANDS': 'Virgin Islands', 'WASHINGTON DC': 'Washington, DC',
  'LOW-VALUED IMPORTS': 'Low-valued imports',
};
const title = (s: string) => s.toLowerCase().replace(/(^|[\s-/])([a-z])/g, (_m, a, b) => a + b.toUpperCase()).replace(/\bSt\b/g, 'St.');
export function regionName(p: Pays, r: string): string {
  if (p === 'bra') return r;
  if (DIST_FIX[r]) return DIST_FIX[r];
  const m = r.match(/^(.*) ([A-Z]{2})$/);
  return m ? `${title(m[1])}, ${m[2]}` : title(r);
}

// ── Bureaux de douane brésiliens : « 0817800 - PORTO DE SANTOS » → « Porto de Santos » ──
const KEEP_UP = new Set(['ALF', 'IRF', 'DRF', 'EADI', 'SP', 'RJ']);
export function portName(urf: string): string {
  const s = urf.replace(/^\d+\s*-\s*/, '');
  return s.split(/\s+/).map((w, i) => KEEP_UP.has(w) ? w : (i > 0 && ['DE', 'DO', 'DA', 'DOS', 'DAS'].includes(w) ? w.toLowerCase() : w.charAt(0) + w.slice(1).toLowerCase())).join(' ');
}
